// resources/js/Pages/Formula/questions/questionUtils.js

// In questionUtils.js - replace the stub with:
import { getAvailableVariablesByInput } from '../functionRegistry';

export function getVariablePreview(preset) {
  if (!preset || !preset.data) return [];

  const mockQuestions = [{
    variable_name: preset.variable_suggestion || 'test_var',
    data: preset.data
  }];

  const grouped = getAvailableVariablesByInput(mockQuestions);
  const allVariables = [];

  Object.values(grouped).forEach(group => {
    group.forEach(variable => {
      allVariables.push(variable.name);
    });
  });

  return allVariables;
}

/**
 * Auto-generate variable name from question label
 */
export function suggestVariableName(label) {
  if (!label) return '';

  // Common question patterns
  const patterns = [
    /what\s+(\w+)/i,           // "What feeling" → "feeling"
    /which\s+(\w+)/i,          // "Which character" → "character"  
    /how\s+(\w+)/i,            // "How intense" → "intensity"
    /(\w+)\s+do\s+you\s+want/i, // "feeling do you want" → "feeling"
  ];

  // Try patterns first
  for (let pattern of patterns) {
    const match = label.match(pattern);
    if (match) return cleanVariableName(match[1]);
  }

  // Fallback: extract meaningful words
  const meaningful = label
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // remove punctuation
    .split(' ')
    .filter(word => !['what', 'which', 'how', 'do', 'you', 'want', 'to', 'the', 'a', 'an', 'is'].includes(word))
    .slice(0, 2); // take first 2 meaningful words

  return meaningful.length > 0 ? meaningful.join('_') : 'question';
}

/**
 * Clean variable name to follow conventions (for real-time typing)
 * Less aggressive - only removes clearly invalid characters
 */
export function cleanVariableName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '') // Remove invalid chars but keep underscores
    .replace(/_{3,}/g, '__'); // Replace 3+ underscores with 2 (less aggressive)
}

/**
 * Final cleanup for variable name (when done typing/closing modal)
 */
export function finalCleanVariableName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '') // Remove invalid chars
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading and trailing underscores
    .replace(/^[0-9]+/, ''); // Remove leading numbers
}

/**
 * Validate variable name
 */
export function validateVariableName(name, existingNames = []) {
  if (!name) return 'Variable name is required';
  if (!/^[a-z][a-z0-9_]*$/i.test(name)) return 'Variable name must start with letter and contain only letters, numbers, and underscores';
  if (existingNames.includes(name)) return 'Variable name already exists';
  return null;
}

/**
 * Update variable references in prompt text
 */
export function updateVariableReferences(promptText, oldName, newName) {
  if (!promptText || !oldName || !newName) return { updatedPrompt: promptText, changeCount: 0 };

  const regex = new RegExp(`{{${oldName}}}`, 'g');
  const matches = promptText.match(regex) || [];
  const updatedPrompt = promptText.replace(regex, `{{${newName}}}`);

  return {
    updatedPrompt,
    changeCount: matches.length
  };
}

/**
 * Check if variable is used in prompt
 */
export function isVariableUsed(promptText, variableName) {
  if (!promptText || !variableName) return false;
  return new RegExp(`{{${variableName}}}`, 'g').test(promptText);
}

/**
 * Get all variables used in prompt text
 */
export function getUsedVariables(promptText) {
  if (!promptText) return [];
  const matches = promptText.match(/{{([^}]+)}}/g) || [];
  return matches.map(match => match.slice(2, -2)); // remove {{ }}
}

/**
 * System variables that are always available
 */
export const SYSTEM_VARIABLES = [
  { name: 'title', description: 'Screenplay title' },
  { name: 'summary', description: 'Screenplay summary' },
  { name: 'page_count', description: 'Number of pages' },
  { name: 'genre', description: 'Screenplay genre' },
  { name: 'author', description: 'Author name' },
  { name: 'logline', description: 'Screenplay logline' },
  { name: 'character_count', description: 'Number of characters' },
  { name: 'scene_count', description: 'Number of scenes' },
];

/**
 * Validate that all variables in prompt text are defined in questions or system variables
 */
export function validatePromptVariables(promptText, questions = []) {
  const usedVariables = getUsedVariables(promptText);
  const questionVariables = questions.map(q => q.variable_name);
  const systemVariableNames = SYSTEM_VARIABLES.map(v => v.name);
  let allValidVariables = [...questionVariables, ...systemVariableNames];

  // Auto-detect registry variables based on question types
  for (const q of questions) {
    if (q.data === 'get_script_scenes_for_drop_down' || q.type === 'scene_text') {
      if (q.data === 'get_script_scenes_for_drop_down' && q.variable_name) {
        allValidVariables.push(q.variable_name + '_text');
      }
      allValidVariables.push(
        'scene_summary', 'scene_themes', 'scene_characters',
        'scene_emotions', 'scene_title', 'scene_setting', 'scene_text'
      );
    }
    if (q.data === 'get_characters_for_drop_down') {
      allValidVariables.push(
        'character_description', 'character_arc', 'character_scenes',
        'character_scene_summaries', 'character_scene_texts',
        'character_internal_goals',
        'character_external_goals', 'character_obstacles'
      );
    }
    if (q.data === 'get_themes_for_drop_down') {
      allValidVariables.push(
        'theme_description', 'theme_symbols', 'theme_scenes', 'theme_character_connections'
      );
    }
    if (q.data === 'get_tropes_for_drop_down') {
      allValidVariables.push(
        'trope_description', 'trope_examples', 'trope_usage', 'trope_effectiveness'
      );
    }
    if (q.data === 'get_philosophical_conflict_for_drop_down') {
      allValidVariables.push(
        'conflict_description', 'conflict_resolution', 'conflict_resolution_location'
      );
    }
    if (q.data === 'get_internal_goals_for_drop_down') {
      allValidVariables.push(
        'internal_goal_description', 'internal_goal_stakes_resolution', 'internal_goal_resolution_location'
      );
    }
    if (q.data === 'get_external_goals_for_drop_down') {
      allValidVariables.push(
        'external_goal_description', 'external_goal_stakes_resolution', 'external_goal_resolution_location'
      );
    }
  }

  allValidVariables = [...new Set(allValidVariables)];

  const undefinedVariables = usedVariables.filter(variable =>
    !allValidVariables.includes(variable)
  );

  if (undefinedVariables.length > 0) {
    return {
      isValid: false,
      undefinedVariables,
      message: `These variables are used in the prompt but not defined: ${undefinedVariables.map(v => `{{${v}}}`).join(', ')}`
    };
  }

  return { isValid: true };
}

// Add variable_type to existing PRESET_QUESTIONS
export const PRESET_QUESTIONS = [
  {
    id: 'scene_selection',
    label: 'Scene Selection',
    default_question: 'Which scene do you want to work on?',
    variable_suggestion: 'scene_number',
    variable_type: 'scene',  // NEW
    type: 'dropdown',
    data: 'get_script_scenes_for_drop_down',
    icon: '🎬'
  },

  // START: ADD THIS NEW OBJECT
  {
    id: 'scene_text_input',
    label: 'Scene Text Input',
    // The default question text that appears in the editor
    default_question: 'Which scene\'s text do you want to load and edit?',
    // The default variable name for the prompt, e.g., {{scene_text}}
    variable_suggestion: 'scene_text',
    // This can be used by other parts of the UI, 'scene' is appropriate
    variable_type: 'scene_text',
    // This is the UNIQUE TYPE that our PHP FormulaPresets helper will look for
    type: 'scene_text',
    // This preset doesn't use a simple data function, so this is null
    data: null,
    // The icon for the selection modal
    icon: '📄'
  },
  // END: ADD THIS NEW OBJECT

  {
    id: 'character_selection',
    label: 'Character Selection',
    default_question: 'Which character are you focusing on?',
    variable_suggestion: 'character_name',
    variable_type: 'character',  // NEW
    type: 'dropdown',
    data: 'get_characters_for_drop_down',
    icon: '👤'
  },
  {
    id: 'theme_selection',
    label: 'Theme Selection',
    default_question: 'Which theme do you want to strengthen?',
    variable_suggestion: 'theme_name',
    variable_type: 'theme',  // NEW
    type: 'dropdown',
    data: 'get_themes_for_drop_down',
    allow_custom: true,
    icon: '💭'
  },
  {
    id: 'trope_selection',
    label: 'Trope Selection',
    default_question: 'Which trope do you want to analyze?',
    variable_suggestion: 'trope_name',
    variable_type: 'trope',  // NEW
    type: 'dropdown',
    data: 'get_tropes_for_drop_down',
    icon: '🎭'
  },
  {
    id: 'philosophical_conflict_selection',
    label: 'Philosophical Conflict',
    default_question: 'Which philosophical conflict do you want to explore?',
    variable_suggestion: 'philosophical_conflict_name',
    variable_type: 'philosophical_conflict',
    type: 'dropdown',
    data: 'get_philosophical_conflict_for_drop_down',
    allow_custom: true,
    icon: '⚖️'
  },
  {
    id: 'internal_goal_selection',
    label: 'Internal Goal',
    default_question: 'Which internal goal do you want to focus on?',
    variable_suggestion: 'internal_goal_name',
    variable_type: 'internal_goal',
    type: 'dropdown',
    data: 'get_internal_goals_for_drop_down',
    allow_custom: true,
    icon: '🧠'
  },
  {
    id: 'external_goal_selection',
    label: 'External Goal',
    default_question: 'Which external goal do you want to focus on?',
    variable_suggestion: 'external_goal_name',
    variable_type: 'external_goal',
    type: 'dropdown',
    data: 'get_external_goals_for_drop_down',
    allow_custom: true,
    icon: '🎯'
  }
];
